import React, { useState } from 'react';
import { Form, Button, Header, Segment, Message, Rating, TextArea, Divider } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { RateableGame, SubmitRatingDto } from '../../app/models/UserRatingModels';
import { useStore } from '../../app/stores/store';
import { socialSkillOptions } from '../../app/common/options/socialSkillOptions';
import { sportSkillOptions } from '../../app/common/options/sportSkillOptions';

interface RateUserTabProps {
    userId: string;
    rateableGame: RateableGame;
    onSuccess: () => void;
    isMobile: boolean;
}

export default observer(function RateUserTab({
    userId,
    rateableGame,
    onSuccess,
    isMobile
}: RateUserTabProps) {
    const { t } = useTranslation();
    const { userRatingStore } = useStore();
    const { submitting, submitRating } = userRatingStore;

    const [socialRatings, setSocialRatings] = useState<{ [key: string]: number }>({});
    const [sportRatings, setSportRatings] = useState<{ [key: string]: number }>({});
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Helper function to get translation key from label
    const getSocialSkillTranslationKey = (label: string) => {
        const labelToKey: { [key: string]: string } = {
            'Fair Play': 'fairplay',
            'On Time': 'ontime',
            'Chill': 'chill',
            'Friendly': 'friendly',
            'Teamwork': 'teamwork'
        };
        return labelToKey[label] || label.toLowerCase().replace(/\s+/g, '');
    };

    const getSportSkillTranslationKey = (label: string) => {
        const labelToKey: { [key: string]: string } = {
            'Speed': 'speed',
            'Dribbling': 'dribbling',
            'Shooting': 'shooting',
            'Defense': 'defense',
            'Strength': 'strength',
            'Passing': 'passing',
            'Ball Control': 'ballcontrol',
            'Positioning': 'positioning',
            'Stamina': 'stamina',
            'Consistency': 'consistency',
            'Pace': 'pace',
            'Endurance': 'endurance',
            'Posture': 'posture',
            'Awareness': 'awareness',
            'Tacking/Jibing': 'tackingjibing',
            'Climbing': 'climbing',
            'Accuracy': 'accuracy',
            'Style': 'style',
            'Parallel Turns': 'parallelturns',
            'Game Sense': 'gamesense',
            'Discipline': 'discipline',
            'Volley': 'volley',
            'Throwing Accuracy': 'throwingaccuracy',
            'Tackling': 'tackling',
            'Reaction Speed': 'reactionspeed',
            'Fielding': 'fielding',
            'Expression': 'expression',
            'Precision': 'precision',
            'Spiking': 'spiking',
            'Maneuvering': 'maneuvering',
            'Route Reading': 'routereading',
            'Stick Handling': 'stickhandling',
            'Technique': 'technique',
            'Spatial Awareness': 'spatialawareness',
            'Pole Planting': 'poleplanting',
            'Flexibility': 'flexibility',
            'Puck Handling': 'puckhandling',
            'Teamwork': 'teamwork',
            'Confidence': 'confidence',
            'Grip Control': 'gripcontrol',
            'Power': 'power',
            'Carving': 'carving',
            'Hitting': 'hitting',
            'Serve': 'serve',
            'Edge Control': 'edgecontrol',
            'Breathing': 'breathing',
            'Navigation': 'navigation',
            'Boat Handling': 'boathandling',
            'Efficiency': 'efficiency',
            'Spare Shooting': 'spareshooting',
            'Rhythm': 'rhythm',
            'Timing': 'timing',
            'Control': 'control',
            'Paddling': 'paddling',
            'Coordination': 'coordination',
            'Teamplay': 'teamplay',
            'Board Control': 'boardcontrol',
            'Team Strategy': 'teamstrategy',
            'Wave Reading': 'wavereading',
            'Blade Control': 'bladecontrol',
            'Spin Control': 'spincontrol',
            'Water Reading': 'waterreading',
            'Turning': 'turning',
            'Focus': 'focus',
            'Batting': 'batting',
            'Striking': 'striking',
            'Dodging': 'dodging',
            'Finishing': 'finishing',
            'Skating': 'skating',
            'Smash': 'smash',
            'Field Awareness': 'fieldawareness',
            'Jumping': 'jumping',
            'Core Strength': 'corestrength',
            'Speed Control': 'speedcontrol',
            'Breathing Control': 'breathingcontrol',
            'Form': 'form',
            'Paddling Technique': 'paddlingtechnique',
            'Reflexes': 'reflexes',
            'Strategy': 'strategy',
            'Checking': 'checking',
            'Wind Reading': 'windreading',
            'Net Play': 'netplay',
            'Footwork': 'footwork',
            'Reaction Time': 'reactiontime',
            'Blocking': 'blocking',
            'Cradling': 'cradling',
            'Agility': 'agility',
            'Trick Execution': 'trickexecution',
            'Communication': 'communication',
            'Preparedness': 'preparedness',
            'Serving': 'serving',
            'Cutting': 'cutting',
            'Balance': 'balance',
            'Forehand': 'forehand',
            'Bowling': 'bowling',
            'Pace Control': 'pacecontrol',
            'Memorization': 'memorization',
            'Putting': 'putting',
            'Commitment': 'commitment',
            'Stability': 'stability',
            'Punching': 'punching',
            'Adaptability': 'adaptability',
            'Swing Control': 'swingcontrol',
            'Throwing': 'throwing',
            'Sail Trim': 'sailtrim',
            'Grappling': 'grappling',
            'Synchronization': 'synchronization',
            'Fluidity': 'fluidity',
            'Shot Accuracy': 'shotaccuracy',
            'Backhand': 'backhand',
            'Placement': 'placement',
            'Ground Balls': 'groundballs',
            'Catching': 'catching'
        };
        return labelToKey[label] || label.toLowerCase().replace(/\s+/g, '');
    };



    const handleSubmit = async () => {
        setError(null);

        const allSocialSkillsRated = socialSkillOptions.every(skill => socialRatings[skill.value] > 0);
        const sportSkills = sportSkillOptions[rateableGame.sportId] || [];
        const allSportSkillsRated = sportSkills.every(skill => sportRatings[skill.value] > 0);

        if (!allSocialSkillsRated || !allSportSkillsRated) {
            setError(t('userRatings.allSkillsRequired'));
            return;
        }

        const dto: SubmitRatingDto = {
            ratedUserId: userId,
            gameId: rateableGame.gameId,
            socialSkillRatings: socialSkillOptions.map(skill => ({
                socialSkillId: skill.value,
                value: socialRatings[skill.value]
            })),
            sportSkillRatings: sportSkills.map(skill => ({
                sportSkillId: skill.value,
                value: sportRatings[skill.value]
            })),
            comment: comment.trim() || undefined
        };

        try {
            await submitRating(dto);
            onSuccess();
        } catch (err) {
            setError(t('userRatings.submitError'));
        }
    };

    const sportSkills = sportSkillOptions[rateableGame.sportId] || [];
    const gameDate = new Date(rateableGame.gameDate).toLocaleDateString();

    return (
        <div style={{ padding: isMobile ? '5px' : '10px' }}>
            <Message info>
                <Message.Header>{t('userRatings.rateForGame')}</Message.Header>
                <p style={{ fontSize: isMobile ? '0.9rem' : undefined }}>
                    <strong>{t('userRatings.gameInfo', { sport: rateableGame.sportName, game: rateableGame.gameName })}</strong><br />
                    <strong>{t('userRatings.gameDate', { date: gameDate })}</strong>
                </p>
            </Message>

            {error && (
                <Message negative>
                    <Message.Header>{t('userRatings.errorTitle')}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <Form onSubmit={handleSubmit}>
                <Segment style={{ padding: isMobile ? '10px' : undefined }}>
                    <Header as={isMobile ? 'h4' : 'h3'} color='teal' dividing>
                        {t('userRatings.socialSkills')}
                    </Header>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? '10px' : '15px'
                    }}>
                        {socialSkillOptions
                            .slice()
                            .sort((a, b) => a.sortIndex - b.sortIndex)
                            .map(skill => (
                                <div
                                    key={skill.value}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: isMobile ? '6px' : '10px',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: '4px',
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                        gap: isMobile ? '8px' : '0'
                                    }}
                                >
                                    <span style={{
                                        fontWeight: 'bold',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        flex: isMobile ? '1 1 100%' : '1',
                                        minWidth: isMobile ? '100%' : 'auto'
                                    }}>
                                        {t(`socialSkills.${getSocialSkillTranslationKey(skill.label)}`)}
                                    </span>
                                    <Rating
                                        icon='star'
                                        rating={socialRatings[skill.value] || 0}
                                        maxRating={5}
                                        onRate={(e, { rating }) =>
                                            setSocialRatings({ ...socialRatings, [skill.value]: rating as number })
                                        }
                                        size={isMobile ? 'large' : 'huge'}
                                        style={{
                                            display: 'flex',
                                            justifyContent: isMobile ? 'center' : 'flex-end',
                                            width: isMobile ? '100%' : 'auto'
                                        }}
                                    />
                                </div>
                            ))}
                    </div>
                </Segment>

                <Segment style={{ padding: isMobile ? '10px' : undefined }}>
                    <Header as={isMobile ? 'h4' : 'h3'} color='blue' dividing>
                        {t('userRatings.sportSkills', { sport: rateableGame.sportName })}
                    </Header>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobile ? '10px' : '15px'
                    }}>
                        {sportSkills
                            .slice()
                            .sort((a, b) => a.sortIndex - b.sortIndex)
                            .map(skill => (
                                <div
                                    key={skill.value}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: isMobile ? '6px' : '10px',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: '4px',
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                        gap: isMobile ? '8px' : '0'
                                    }}
                                >
                                    <span style={{
                                        fontWeight: 'bold',
                                        fontSize: isMobile ? '0.85rem' : '1rem',
                                        flex: isMobile ? '1 1 100%' : '1',
                                        minWidth: isMobile ? '100%' : 'auto'
                                    }}>
                                        {t(`sportSkills.${getSportSkillTranslationKey(skill.label)}`)}
                                    </span>
                                    <Rating
                                        icon='star'
                                        rating={sportRatings[skill.value] || 0}
                                        maxRating={5}
                                        onRate={(e, { rating }) =>
                                            setSportRatings({ ...sportRatings, [skill.value]: rating as number })
                                        }
                                        size={isMobile ? 'large' : 'huge'}
                                        style={{
                                            display: 'flex',
                                            justifyContent: isMobile ? 'center' : 'flex-end',
                                            width: isMobile ? '100%' : 'auto'
                                        }}
                                    />
                                </div>
                            ))}
                    </div>
                </Segment>

                <Divider />

                <Segment style={{ padding: isMobile ? '10px' : undefined }}>
                    <Header as={isMobile ? 'h5' : 'h4'}>
                        {t('userRatings.addComment')} ({t('userRatings.optional')} & {t('userRatings.anonymous')})
                    </Header>
                    <p style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', color: '#666' }}>
                        {t('userRatings.commentInfo')}
                    </p>
                    <Form.Field>
                        <TextArea
                            placeholder={t('userRatings.commentPlaceholder')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={isMobile ? 3 : 4}
                            maxLength={500}
                            style={{ fontSize: isMobile ? '0.9rem' : undefined }}
                        />
                        <div style={{ textAlign: 'right', fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#999', marginTop: '5px' }}>
                            {t('userRatings.commentLength', { current: comment.length, max: 500 })}
                        </div>
                    </Form.Field>
                </Segment>

                <Button
                    type='submit'
                    color='teal'
                    fluid
                    loading={submitting}
                    disabled={submitting}
                    size={isMobile ? 'medium' : 'large'}
                >
                    {t('userRatings.submitRating')}
                </Button>
            </Form>
        </div>
    );
});